import { FileText } from 'lucide-react';
import { Bubble, ChatThread } from '@/components/ui/Chat';
import { Spinner } from '@/components/ui/Feedback';
import { Markdown } from '@/components/ui/Markdown';
import type { AiChatAttachment, AiChatMessage } from '@/api/miniapp/types';
import { SentFile, VoicePlayback } from './VoiceParts';
import { lessonFileLabel } from '@/lib/fileName';

/**
 * Лента переписки с ассистентом.
 *
 * Все сообщения — один список: отдельный список неподтверждённых ставил ответ
 * агента выше собственной реплики, и переписка выглядела перепутанной.
 */
export function ChatMessages({
  messages,
  sending,
  awaitingReply,
  runPending,
  onOpenAttachment,
}: {
  messages: AiChatMessage[];
  sending: boolean;
  awaitingReply: boolean;
  runPending: boolean;
  onOpenAttachment: (attachment: AiChatAttachment) => void;
}) {
  return (
    <ChatThread messages={messages}>
      {messages.map((message, index) => (
        <Bubble
          key={`${message.run_id ?? 'msg'}-${index}`}
          // Неподтверждённое сообщение приглушено: видно, что оно ещё в пути
          className={message.pending ? 'opacity-70' : undefined}
          role={message.role === 'user' ? 'user' : 'assistant'}
          title={message.role === 'user' ? undefined : 'ИИ-ассистент'}
          footer={<MessageFooter message={message} onOpenAttachment={onOpenAttachment} />}
        >
          {message.role === 'user' ? (
            message.content || (message.audioUrl ? 'Голосовое сообщение' : '')
          ) : (
            // Агент отвечает разметкой: без разбора видны служебные звёздочки
            <Markdown text={message.content} />
          )}
        </Bubble>
      ))}

      {sending || awaitingReply ? (
        <Bubble role="assistant" title="ИИ-ассистент">
          <span className="inline-flex items-center gap-2">
            <Spinner size={14} />
            {sending ? 'Отправляю сообщение…' : 'Ассистент печатает…'}
          </span>
        </Bubble>
      ) : null}

      {runPending ? (
        <Bubble role="assistant" title="ИИ-ассистент">
          Собираю итоговый материал по вашим ответам. Это занимает до нескольких минут —
          файл появится здесь, не закрывайте экран.
        </Bubble>
      ) : null}
    </ChatThread>
  );
}

function MessageFooter({
  message,
  onOpenAttachment,
}: {
  message: AiChatMessage;
  onOpenAttachment: (attachment: AiChatAttachment) => void;
}) {
  if (message.audioUrl) {
    return (
      <VoicePlayback url={message.audioUrl} durationMs={message.voiceMs} pending={message.pending} />
    );
  }
  if (message.sentFile) return <SentFile name={message.sentFile} pending={message.pending} />;
  if (!message.attachments?.length) return null;

  return (
    <div className="mt-2 grid gap-1.5">
      {message.attachments.map((attachment) => (
        <AttachmentButton
          key={attachment.file_id}
          attachment={attachment}
          onOpen={onOpenAttachment}
        />
      ))}
    </div>
  );
}

/** Вложение открывается внутри приложения, поэтому это кнопка, а не ссылка. */
export function AttachmentButton({
  attachment,
  onOpen,
  className,
}: {
  attachment: AiChatAttachment;
  onOpen: (attachment: AiChatAttachment) => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={() => onOpen(attachment)}
      className={
        className ??
        'bd-focus flex items-center gap-2 rounded-control border border-line/70 bg-surface px-2.5 py-2 text-left text-[12px] text-ink'
      }
    >
      <FileText className="h-4 w-4 shrink-0 text-muted" aria-hidden />
      {/* Служебное имя итога человеку ничего не говорит — показываем понятное */}
      <span className="truncate">{lessonFileLabel({ name: attachment.filename }, 0)}</span>
    </button>
  );
}
